import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { readLinkToken, clearLinkToken } from "@/lib/linkToken";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import { api, formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [token] = useState(() => readLinkToken("/reset-password"));
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      clearLinkToken("/reset-password");
      setDone(true);
      toast.success("Password updated!");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid link" subtitle="This password reset link is missing or invalid."
        footer={<Link to="/forgot-password" className="text-[#2563EB] font-medium">Request a new link</Link>}>
        <div className="rounded-md border border-red-200 bg-red-50 p-5 text-sm text-red-700" data-testid="reset-invalid">
          The reset token was not found in the URL. Please request a new reset link.
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set a new password" subtitle="Choose a strong password you haven't used before.">
      {done ? (
        <div className="rounded-md border border-green-200 bg-green-50 p-5 flex gap-3" data-testid="reset-success">
          <CheckCircle2 className="text-green-600 shrink-0" size={22} />
          <div>
            <p className="font-medium text-green-800">Password updated</p>
            <p className="text-sm text-green-700 mt-1">Redirecting you to sign in…</p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">New password</label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password-input"
              className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none"
              placeholder="At least 8 characters"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Confirm password</label>
            <input
              type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm-input"
              className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none"
              placeholder="Re-enter password"
            />
          </div>
          <button
            type="submit" disabled={loading}
            data-testid="reset-submit-button"
            className="w-full h-11 rounded-md bg-[#2563EB] text-white font-medium hover:bg-[#1D4ED8] transition-colors flex items-center justify-center disabled:opacity-60"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Update password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
